import { FC, useState } from 'react';
import { useForm, FormErrors } from '@mantine/form';
import {
  Box,
  Button,
  SimpleGrid,
  UnstyledButton,
  TextInput,
  LoadingOverlay,
  Text,
  Flex
} from '@mantine/core';
import { IconPlus } from '@tabler/icons';
import { useStyles } from './styles';
import { themeColors } from '@/theme/colors';
import { SelectWithPagination } from '@/containers/pages/filials/components/select';
import { SelectUserModal } from '@components/modal/select-user';
import { SelectGroupSimpleModal } from '@components/modal/select-group-simple';
import { showNotification } from '@mantine/notifications';
import { IUserTableData } from '@/entities/admin-app/users';
import { useAddRolesMutation } from '@/entities/admin-app/roles/api';
import { Badges } from '../badges';
import { transformObjectIfKeyIsNull } from '@/containers/pages/roles/utils';
import { useTranslation } from 'react-i18next';
import { getValidateErrors } from '@/lib/utils/validation';
import { validateCreateRolesSchema } from '@/containers/pages/roles/components/schemes/validate';
import { SelectQuotas } from '@/containers/pages/user/components/Quotaselect';
import { SelectGroupModal } from '@/components/modal/select-group';
import { IListItem } from '@/types';
import { IGroup } from '@/entities/admin-app/user';

interface IRolesCreateForms {
  close(): void;
  onSuccess?(): void;
}

interface ICreateRole {
  Name: string;
  Customer: {
    Id: null;
    Name: string | null;
  };
  Parent: {
    Id: number | null;
  };
  Owner: {
    Id: number | null;
    Name: string | null;
  };
  Quota: {
    Id: number | null;
    Name: string | null;
  };
  AllowedRoles: {
    Id: number;
    Name: string;
  }[];
  HasSelfViewAccess: boolean;
}
export interface IBadges {
  label: string;
}
export const RolesCreateForm: FC<IRolesCreateForms> = ({ close, onSuccess }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isAllowedRoleModalOpen, setIsAllowedRoleModalOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState<IUserTableData | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<IBadges | null>(null);
  const [selectedAllowedRoles, setSelectedAllowedRoles] = useState<IListItem>({} as IListItem);
  const [createRoles, { isLoading }] = useAddRolesMutation();

  const closeModals = () => {
    setIsUserModalOpen(false);
    setIsRoleModalOpen(false);
    setIsAllowedRoleModalOpen(false);
  };

  const handleConfirmSelectUserModal = async () => {
    if (!selectedUser) {
      return;
    }
    form.setFieldValue('Owner.Id', selectedUser?.Id ?? null);
    form.setFieldValue('Owner.Name', selectedUser?.Name ?? null);
    closeModals();
  };

  const handleCloseSelectUserModal = () => {
    setIsUserModalOpen(false);
  };

  const handleCloseAllowedRoleModal = () => {
    setIsAllowedRoleModalOpen(false);
  };

  const handleRemoveRoles = () => {
    setSelectedRoles(null);
    form.setFieldValue('Parent.Id', null);
  };

  const handleRemoveAllowedRole = (item: IGroup) => {
    const index = form.values.AllowedRoles.findIndex((i) => i.Id === item.Id);
    form.removeListItem('AllowedRoles', index);
  };

  const handleRemoveUser = () => {
    setSelectedUser(null);
    form.setFieldValue('Owner.Id', null);
    form.setFieldValue('Owner.Name', null);
  };

  const handleRemoveSelfViewAccess = () => {
    form.setFieldValue('HasSelfViewAccess', false);
  };

  const setGroupId = async (id: number) => {
    form.setFieldValue('Parent.Id', id);
    closeModals();
  };

  const setGroup = async (group: IBadges) => {
    setSelectedRoles(group);
  };

  const handleAddAllowedRoles = (roles: IGroup[]) => {
    roles.forEach((role) => {
      form.insertListItem('AllowedRoles', role);
    });

    setIsAllowedRoleModalOpen(false);
  };

  const form = useForm<ICreateRole>({
    initialValues: {
      Name: '',
      Customer: {
        Id: null,
        Name: null
      },
      Parent: {
        Id: null
      },
      Owner: {
        Id: null,
        Name: null
      },
      Quota: {
        Id: null,
        Name: ''
      },
      AllowedRoles: [],
      HasSelfViewAccess: true
      //Type: 0,
    },
    validateInputOnChange: true,
    validate: (values: ICreateRole): FormErrors =>
      getValidateErrors(validateCreateRolesSchema(t), values)
  });

  const isDisabled = Object.keys(form.errors).length !== 0 || !form.isDirty();

  const handleSubmit = async (values: any) => {
    const formatted = transformObjectIfKeyIsNull(JSON.parse(JSON.stringify(values)));
    try {
      const res = await createRoles(formatted);
      if (!Object.keys(res).includes('error')) {
        showNotification({
          title: t('roles.roleAdded'),
          message: '',
          autoClose: true,
          color: 'green'
        });
        onSuccess && onSuccess();
        handleClose();
      } else {
        showNotification({
          title: `${(res as any)?.error?.data}`,
          message: '',
          autoClose: true,
          color: 'red'
        });
      }
    } catch (err: any) {
      console.warn('Create roles ERROR', err);
    }
  };

  const handleClose = () => {
    close();
    form.reset();
  };

  return (
    <>
      <form className={classes.modalRoot} onSubmit={form.onSubmit(handleSubmit)}>
        <Box className={classes.modalContainer}>
          <SimpleGrid cols={1} w="100%">
            <TextInput
              withAsterisk
              label={t('roles.name')}
              placeholder=""
              {...form.getInputProps('Name')}
            />
            <SelectWithPagination
              form={form}
              fieldName="Customer.Id"
              label={t('roles.addFilial')}
              placeholder={t('choose')}
              list={[]}
              defaultValue={form.getInputProps('Customer.Id').value || null}
              value={form.getInputProps('Customer.Id').value}
              isRoleComponent
              filialName={form.getInputProps('Customer.Name').value}
              customerField="Customer.Name"
            />
            <SelectQuotas
              form={form}
              fieldName="Quota.Id"
              quotaField="Quota.Name"
              Customer={form.getInputProps('Customer.Id').value}
              value={form.values.Quota.Id}
              QuotaName={form.values.Quota.Name}
            />

            <SimpleGrid cols={1} spacing="xs">
              <Text size="sm" fw="500">
                {t('roles.whoCanShareAccess')}
              </Text>
              <Button
                className={classes.addButton}
                size="sm"
                variant="outline"
                rightSection={<IconPlus color={themeColors.brandGray[0]} />}
                onClick={() => setIsAllowedRoleModalOpen(true)}
              >
                {t('roles.addAllowedRoles')}
              </Button>
              <Flex gap="xs" wrap="wrap">
                {form.values.HasSelfViewAccess && form.values.Name && (
                  <Box>
                    <Badges label={form.values.Name} onRemove={() => handleRemoveSelfViewAccess} />
                  </Box>
                )}
                {form.values.AllowedRoles &&
                  form.values.AllowedRoles?.map((item) => (
                    <Box key={item.Id}>
                      <Badges label={item.Name} onRemove={() => handleRemoveAllowedRole(item)} />
                    </Box>
                  ))}
              </Flex>
            </SimpleGrid>

            <SimpleGrid cols={1} spacing="xs">
              <Button
                className={classes.addButton}
                size="sm"
                variant="outline"
                rightSection={<IconPlus color={themeColors.brandGray[0]} />}
                onClick={() => setIsRoleModalOpen(true)}
              >
                {t('roles.addParentRole')}
              </Button>
              {selectedRoles && (
                <Box>
                  <Badges label={selectedRoles?.label} onRemove={handleRemoveRoles} />
                </Box>
              )}
            </SimpleGrid>
            <SimpleGrid cols={1} spacing="xs">
              <Button
                className={classes.addButton}
                size="sm"
                variant="outline"
                rightSection={<IconPlus color={themeColors.brandGray[0]} />}
                onClick={() => setIsUserModalOpen(true)}
              >
                {t('roles.addRoleManager')}
              </Button>
              {form.values.Owner.Name && (
                <Box>
                  <Badges label={form.values.Owner.Name} onRemove={handleRemoveUser} />
                </Box>
              )}
            </SimpleGrid>
          </SimpleGrid>
        </Box>

        <Box className={classes.modalFooter}>
          <Button type="submit" size="sm" radius={8} w="49%" disabled={isDisabled}>
            {t('create')}
          </Button>

          <UnstyledButton
            type="button"
            ml={8}
            className={classes.modalCancelBtn}
            w="49%"
            onClick={close}
          >
            {t('cancel')}
          </UnstyledButton>
        </Box>
        <SelectGroupSimpleModal
          onClose={closeModals}
          isOpened={isRoleModalOpen}
          title={t('roles.addParentRole')}
          setGroupId={setGroupId}
          setGroup={setGroup}
        />

        <SelectGroupModal
          isOpened={isAllowedRoleModalOpen}
          title={t('profile.addRole')}
          onClose={handleCloseAllowedRoleModal}
          setSelectedGroup={setSelectedAllowedRoles}
          selectedGroup={selectedAllowedRoles}
          setGroups={handleAddAllowedRoles}
          groups={form.values.AllowedRoles}
        />

        <SelectUserModal
          multipleUsers={false}
          isOpened={isUserModalOpen}
          title={t('roles.addRoleManager')}
          onClose={closeModals}
          setSelectedUsers={setSelectedUser}
          userId={selectedUser?.Id}
          controlSection
          controlSectionProps={{
            confirmProps: {
              btnName: t('add'),
              fn: handleConfirmSelectUserModal
            },
            cancelProps: {
              btnName: t('cancel'),
              fn: handleCloseSelectUserModal
            }
          }}
        />
      </form>
      <LoadingOverlay visible={isLoading} />
    </>
  );
};
